/**
 * Portfolio — cross-engine services.
 *
 * Everything that operates ABOVE engines (Home, Wallet, Forecast, Executive,
 * Closures) is composed here. This is domain logic that spans multiple
 * engines — not any single engine's responsibility.
 */

/**
 * @param {{
 *   engines: Record<string, any>,
 *   storage: import('./ports').StoragePort,
 *   crm: import('./ports').CRMPort,
 * }} deps
 */
export function createPortfolio({ engines, storage, crm }) {
  return {
    async listEngineSummaries() {
      return Promise.all(Object.values(engines).map((e) => e.getSummary()));
    },

    async listAllOpportunities(filter = {}) {
      // Cross-engine — read from storage directly. Engines don't have to
      // orchestrate each other; the portfolio does.
      return storage.listOpportunities({ ...filter, limit: filter.limit || 500 });
    },

    async listClosures(filter = {}) {
      return storage.listClosures(filter);
    },

    async listTeamMembers() {
      return storage.listTeamMembers();
    },

    async countActors() {
      const actors = await storage.listActors();
      return actors.length;
    },

    async getForecast() {
      const opps = await storage.listOpportunities({ outcome: "pending", limit: 10000 });
      const p50Total = Math.round(
        opps.reduce((s, o) => s + o.estimatedValue.value.amount * o.probability, 0)
      );
      const p10 = Math.round(p50Total * 0.6);
      const p90 = Math.round(p50Total * 1.35);
      const byEngine = {};
      Object.keys(engines).forEach((engineId) => {
        const engOpps = opps.filter((o) => o.engineId === engineId);
        byEngine[engineId] = {
          amount: Math.round(
            engOpps.reduce((s, o) => s + o.estimatedValue.value.amount * o.probability, 0)
          ),
          currency: "USD",
        };
      });
      return {
        horizon: "30d",
        points: [
          { percentile: "p10", value: { amount: p10, currency: "USD" } },
          { percentile: "p50", value: { amount: p50Total, currency: "USD" } },
          { percentile: "p90", value: { amount: p90, currency: "USD" } },
        ],
        byEngine,
        sampleSize: opps.length,
        computedAt: new Date().toISOString(),
      };
    },

    // Ingestion — orchestrates Actor creation + Signal generation + engine evaluation.
    // "Cuenta importada" y "oportunidad detectada" son conteos separados a
    // propósito: una cuenta se guarda siempre que tenga los datos mínimos,
    // tenga o no una oportunidad detrás.
    //
    // Prioridad única entre motores (bug real encontrado en auditoría
    // funcional, agosto 2026): antes cada motor se evaluaba de forma
    // independiente para el mismo actor — un cliente con señal válida de
    // Venta Cruzada pero sin renovación/inactividad explícita podía terminar
    // con una oportunidad de Recuperación Y una de Venta Cruzada
    // simultáneamente, ambas visibles en la Billetera para el mismo
    // cliente. crossSell.js ya cedía prioridad a Renovación/Recuperación
    // internamente, pero Recuperación nunca cedía a Venta Cruzada — la
    // orquestación es el único lugar correcto para garantizar esto de forma
    // centralizada, sin duplicar la regla dentro de cada motor. Orden fijo,
    // ya declarado en crossSell.js: Renovación > Recuperación > Venta
    // Cruzada > el resto. Se detiene en la primera oportunidad real
    // generada — una sola oportunidad principal visible por cliente.
    async ingestActors(rawActors) {
      const ENGINE_PRIORITY_ORDER = [
        "renewal", "recovery", "cross_sell", "upsell", "retention", "referral", "prospecting",
      ];
      const orderedEngines = [
        ...ENGINE_PRIORITY_ORDER.map((id) => engines[id]).filter(Boolean),
        ...Object.values(engines).filter((e) => !ENGINE_PRIORITY_ORDER.includes(e.id)),
      ];

      const warnings = [];
      const crossSellSkipped = [];
      let created = 0;
      let opportunitiesCreated = 0;
      const opportunitiesByEngine = {};
      for (const draft of rawActors) {
        if (!draft.companyName) {
          warnings.push("Actor sin nombre de empresa ignorado");
          continue;
        }

        // Vinculación automática con GHL (P0 Sprint 11 noche): si el actor
        // ya trae un contactId (ej. columna ghl_contact_id del CSV), se
        // respeta tal cual — nunca se re-busca ni se sobreescribe. Si no,
        // se intenta resolver por teléfono/correo, NUNCA por nombre. Un
        // "ambiguous" no elige — se advierte y el actor queda sin vincular,
        // igual que un "not_found".
        if (!draft.externalRefs?.ghlContactId && (draft.phone || draft.email)) {
          try {
            const resolution = await crm.resolveContact({ phone: draft.phone, email: draft.email });
            if (resolution.status === "found") {
              draft.externalRefs = { ...draft.externalRefs, ghlContactId: resolution.contactId };
            } else if (resolution.status === "ambiguous") {
              warnings.push(
                `${draft.companyName}: ${resolution.candidates.length} contactos coinciden en GHL — ` +
                "no se vinculó ninguno automáticamente, revisa manualmente."
              );
            } else if (resolution.status === "error") {
              // Hallazgo real (diagnóstico WhatsApp E2E, agosto 2026): antes
              // de este fix, un fallo real de la búsqueda en GHL (credencial
              // inválida, sin permiso, GHL caído) se veía IDÉNTICO a "este
              // cliente no tiene contacto en GHL" -- se importaba sin
              // vincular y sin ninguna advertencia. Ahora resolve_contact
              // distingue ese caso (ver ghl_client.py) y aquí se avisa en
              // vez de importar en silencio.
              warnings.push(
                `${draft.companyName}: no se pudo confirmar en GHL si existe (falló la búsqueda, no el contacto) — ` +
                "se importó sin vincular, revisa manualmente."
              );
            }
          } catch {
            /* CRM no configurado (adapter "none"/mock) — el actor se importa sin vincular */
          }
        }

        await storage.saveActor(draft);
        created++;
        // Evaluate in priority order, deteniéndose en la primera oportunidad
        // real — nunca se le da a un actor una segunda oportunidad de un
        // motor de menor prioridad una vez que ya tiene una principal.
        for (const engine of orderedEngines) {
          try {
            const opp = await engine.evaluateActor(draft);
            if (opp) {
              opportunitiesCreated++;
              opportunitiesByEngine[engine.id] = (opportunitiesByEngine[engine.id] || 0) + 1;
              break;
            } else if (typeof engine.explainEligibility === "function") {
              // Solo el motor real de Venta Cruzada expone esto (Sprint 12)
              // — nunca silencio: si no generó oportunidad, se reporta por
              // qué, para que el resultado de la importación sea honesto.
              const { reasons } = engine.explainEligibility(draft);
              if (reasons?.length) {
                crossSellSkipped.push({ companyName: draft.companyName, reasons });
              }
            }
          } catch (err) {
            // Hallazgo real (diagnóstico WhatsApp E2E, agosto 2026): este
            // catch se creía protección contra motores "stub" (comentario
            // anterior), pero los stubs actuales (domain/engines/stubs.js)
            // resuelven a undefined, nunca lanzan -- este catch en realidad
            // solo podía estar ocultando un fallo real de un motor activo
            // (renewal/recovery/cross_sell) sin ninguna señal visible en la
            // pantalla de importación. Se reporta igual que cualquier otra
            // advertencia de esta importación -- nunca se detiene el resto
            // del proceso por un motor que falló.
            const errMessage = err?.message || "error desconocido";
            warnings.push(
              `${draft.companyName}: el motor "${engine.id}" no pudo evaluarse (${errMessage}).`
            );
            // Este proceso corre entero en el navegador -- sin esto, la
            // única evidencia de esta falla era la pantalla de importación
            // en el momento exacto en que ocurrió, nunca en logs de
            // servidor. Best-effort: si el propio reporte falla, no debe
            // interrumpir la importación (ver adapters/storage/http.js).
            if (typeof storage.reportClientError === "function") {
              storage.reportClientError(
                "ingest.engine_failed",
                `engine=${engine.id} actorId=${draft.id} message=${errMessage}`
              );
            }
          }
        }
      }
      return {
        rowsReceived: rawActors.length,
        actorsCreated: created,
        opportunitiesCreated,
        opportunitiesByEngine,
        warnings,
        crossSellSkipped,
      };
    },
  };
}
